// apps/client/src/world/camera.ts — fixed 3/4 follow camera, pinch/wheel zoom within limits (§8.3)
import { tuning } from '@whiskerton/content';
import * as THREE from 'three';
import { worldUniforms } from '../engine/shader/worldPatch';
const C = tuning.camera;
export function cameraOffset(pitchDeg: number, distance: number): THREE.Vector3 {
  const p = (pitchDeg * Math.PI) / 180;
  return new THREE.Vector3(0, Math.sin(p) * distance, Math.cos(p) * distance);   // south of the target, looking north (D-07)
}
/** A screen window in normalized device coordinates (-1…1, y up) that framed content must fit inside. */
export interface NdcWindow { l: number; r: number; b: number; t: number }
/** A held view. With `follow`, the camera tracks you at `distance` instead, aiming `ahead` world units north of you (so you stand
 *  in the middle of the screen below the HUD row, not under it), its target kept inside that floor box (x, z). */
export interface Framing { target: THREE.Vector3; distance: number; follow?: { min: THREE.Vector2; max: THREE.Vector2; ahead?: number } }
/** The points of a room that must be on screen: the floor's corners, the top of the back wall, the front of the left wall's top, and a
 *  resident's head at the open front edge. Rooms are drawn on [x, x + w] × [y, y + h] with the back wall at y and the left wall at x. */
export function roomPoints(b: { x: number; y: number; w: number; h: number }, wallHeight: number, head = 1.7): THREE.Vector3[] {
  const x0 = b.x, x1 = b.x + b.w, z0 = b.y, z1 = b.y + b.h, V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
  return [V(x0, 0, z0), V(x1, 0, z0), V(x0, 0, z1), V(x1, 0, z1), V(x0, wallHeight, z0), V(x1, wallHeight, z0), V(x0, wallHeight, z1),
    V(x0, head, z1), V(x1, head, z1)];
}
/** The HUD's top row (52 px round buttons, the pills) below its inset (3.2cqw, tokens.css --hud-inset), plus a little air. */
const HUD_ROW_PX = 56;
/** Where a framed room must fit on a `w` × `h` screen: below the top HUD row, a small margin elsewhere. */
export const indoorWindow = (w: number, h: number): NdcWindow => ({ l: -0.94, r: 0.94, b: -0.94, t: 1 - (2 * (0.032 * w + HUD_ROW_PX)) / h });
/** The 3/4 camera (fixed pitch and fov) that holds every point inside `win`: the nearest distance whose projection fits the window,
 *  aimed so the projection is centred in it. Indoors this frames the whole room instead of following you (M1 playtest: entering at
 *  the door, the room sat in a corner of a desktop screen with the top of its back wall cut off, and under the bells pill on a phone). */
export function frameFor(points: readonly THREE.Vector3[], aspect: number, win: NdcWindow): Framing {
  const cam = new THREE.PerspectiveCamera(C.fovDeg, aspect, 0.3, 500);
  const centre = new THREE.Box3().setFromPoints([...points]).getCenter(new THREE.Vector3());
  const v = new THREE.Vector3();
  const rectAt = (target: THREE.Vector3, d: number) => {
    cam.position.copy(target).add(cameraOffset(C.pitchDeg, d)); cam.lookAt(target); cam.updateMatrixWorld();
    let l = Infinity, r = -Infinity, b = Infinity, t = -Infinity;
    for (const p of points) { v.copy(p).project(cam); l = Math.min(l, v.x); r = Math.max(r, v.x); b = Math.min(b, v.y); t = Math.max(t, v.y); }
    return { l, r, b, t };
  };
  const target = centre.clone();
  const fits = (d: number) => { const q = rectAt(target, d); return q.r - q.l <= win.r - win.l && q.t - q.b <= win.t - win.b; };
  const tan = Math.tan((C.fovDeg * Math.PI) / 360);
  const right = new THREE.Vector3(), up = new THREE.Vector3();
  let distance: number = C.distance;
  for (let round = 0; round < 4; round++) {                       // perspective: fit the size, re-centre, and fit again
    let lo = 1, hi = C.maxDistance * 4;
    for (let i = 0; i < 40; i++) { const mid = (lo + hi) / 2; if (fits(mid)) hi = mid; else lo = mid; }
    distance = hi;
    for (let i = 0; i < 4; i++) {                                 // slide the view until the projection sits in the window's middle
      const q = rectAt(target, distance);
      cam.matrixWorld.extractBasis(right, up, v);
      const dx = (win.l + win.r) / 2 - (q.l + q.r) / 2, dy = (win.b + win.t) / 2 - (q.b + q.t) / 2;
      target.addScaledVector(right, -dx * distance * tan * aspect).addScaledVector(up, -dy * distance * tan);
    }
  }
  return { target, distance: distance * 1.002 };                  // a hair of slack for the last re-centring
}

/** A room's view on a `w` x `h` screen. A room that fits within tuning.camera.roomMaxDistance is held whole (frameFor); a bigger one
 *  (the museum, M2 playtest: the whole 16 x 12 hall on a phone left everyone about 20 px tall) is followed at the outdoor distance,
 *  the target kept far enough inside the room that its walls, not the void, fill the screen edges. */
export function roomFraming(b: { x: number; y: number; w: number; h: number }, wallHeight: number, w: number, h: number): Framing {
  const whole = frameFor(roomPoints(b, wallHeight), w / h, indoorWindow(w, h));
  if (whole.distance <= C.roomMaxDistance) return whole;
  const d = C.distance, tan = Math.tan((C.fovDeg * Math.PI) / 360);
  // The floor's half-width and half-depth on screen at the target, less the margins in tuning.camera.roomFollow (why each, there).
  const F = C.roomFollow;
  const halfW = Math.min(b.w / 2, d * tan * (w / h) * F.sideMargin);
  const halfD = Math.min(b.h / 2, (d * tan) / Math.sin((C.pitchDeg * Math.PI) / 180) * F.depthMargin);
  // The screen's free part is the window below the HUD row, centred lower than the screen. Aimed at you, the camera put you in the
  // screen's middle and the back of the room (the aquarium's back row, v1 round 3) under the pills: aim far enough north of you that
  // you stand a little below the window's middle instead, so more of the room ahead of you shows.
  const win = indoorWindow(w, h), mid = win.t - F.youAt * (win.t - win.b), at = screenProjector({ target: new THREE.Vector3(), distance: d }, w, h);
  let lo = 0, hi = b.h;
  for (let i = 0; i < 30; i++) { const m = (lo + hi) / 2; if (1 - (2 * at(new THREE.Vector3(0, 0, m)).y) / h > mid) lo = m; else hi = m; }
  return { target: whole.target, distance: d,
    follow: { min: new THREE.Vector2(b.x + halfW, b.y + halfD), max: new THREE.Vector2(b.x + b.w - halfW, b.y + b.h - halfD + F.southSlack), ahead: hi } };
}

/** A screen rectangle in CSS pixels. */
export interface PxRect { left: number; top: number; right: number; bottom: number }
/** What decorate mode leaves of the screen for the room: the tray's top edge and the HUD pieces of its top row (the title, Done), and
 *  how much wall to keep in view: all of it on the Wallpaper tab, a band along the floor otherwise (the floor gets the room). */
export interface DecorateUi { trayTop: number; hud: readonly PxRect[]; walls: 'full' | 'low' }
/** The wall shown above the floor on the tabs that place things on the floor (world units). */
const LOW_WALL = 1;
/** Where world points land on a `w` x `h` screen (CSS pixels) under a framing. */
export function screenProjector(f: Framing, w: number, h: number): (p: THREE.Vector3) => { x: number; y: number } {
  const cam = new THREE.PerspectiveCamera(C.fovDeg, w / h, 0.3, 500);
  cam.position.copy(f.target).add(cameraOffset(C.pitchDeg, f.distance)); cam.lookAt(f.target); cam.updateMatrixWorld();
  const v = new THREE.Vector3();
  return (p) => { v.copy(p).project(cam); return { x: ((v.x + 1) / 2) * w, y: ((1 - v.y) / 2) * h }; };
}
/** Decorate mode in a room (M1 playtest, 844 x 390, a 6 x 6 cottage: the tray covered the front floor rows). The room is framed into
 *  the band between the top of the screen and the tray, so every floor row can be tapped above it. Walls may run up under the title
 *  pill and Done (nobody taps a wall); the floor may not. Of the windows that keep the floor clear of them (the full width; between
 *  the title and Done; below the HUD row) the one that shows the floor biggest wins. */
export function decorateFraming(b: { x: number; y: number; w: number; h: number }, wallHeight: number, w: number, h: number, ui: DecorateUi,
  edge = 8): Framing {
  const all = roomPoints(b, ui.walls === 'full' ? wallHeight : Math.min(wallHeight, LOW_WALL)), floor = all.slice(0, 4), points = all.slice(0, 7);
  const bottom = Math.max(ui.trayTop - edge, edge * 6);
  const hudBottom = Math.min(Math.max(edge, ...ui.hud.map((q) => q.bottom + edge)), bottom - edge * 4);
  const leftOf = Math.max(edge, ...ui.hud.filter((q) => q.right <= w / 2).map((q) => q.right + edge));
  const rightOf = Math.min(w - edge, ...ui.hud.filter((q) => q.left >= w / 2).map((q) => q.left - edge));
  const win = (top: number, l = edge, r = w - edge): NdcWindow => ({ l: -1 + (2 * l) / w, r: -1 + (2 * r) / w, t: 1 - (2 * top) / h, b: 1 - (2 * bottom) / h });
  const tries = [win(edge), ...(rightOf - leftOf > w / 4 ? [win(edge, leftOf, rightOf)] : []), win(hudBottom)];
  let best: { f: Framing; depth: number } | null = null;
  for (const t of tries) {
    const f = frameFor(points, w / h, t), ps = floor.map(screenProjector(f, w, h));
    const xs = ps.map((p) => p.x), ys = ps.map((p) => p.y);
    const r = { left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) };
    const clear = ui.hud.every((q) => r.right <= q.left - edge / 2 || r.left >= q.right + edge / 2 || r.bottom <= q.top - edge / 2 || r.top >= q.bottom + edge / 2);
    if ((clear || t === tries.at(-1)) && (!best || r.bottom - r.top > best.depth)) best = { f, depth: r.bottom - r.top };
  }
  return best!.f;
}

export class FollowCamera {
  readonly camera: THREE.PerspectiveCamera;
  readonly target = new THREE.Vector3();
  distance: number = C.distance;
  /** Indoors: the room's framing, held instead of following you; null outdoors. Zoom changes only the outdoor distance. */
  private framing: Framing | null = null;
  constructor(aspect: number) { this.camera = new THREE.PerspectiveCamera(C.fovDeg, aspect, 0.3, 500); }
  follow(p: THREE.Vector3, dt: number): void { this.target.lerp(this.aim(p), 1 - Math.exp(-dt * 8)); this.apply(); }
  snap(p: THREE.Vector3): void { this.target.copy(this.aim(p)); this.apply(); }
  /** Where to look: you outdoors, the held framing indoors, or you kept inside a followed room's box. */
  private aim(p: THREE.Vector3): THREE.Vector3 {
    const f = this.framing;
    if (!f) return p;
    if (!f.follow) return f.target;
    const { min, max, ahead = 0 } = f.follow;
    return new THREE.Vector3(THREE.MathUtils.clamp(p.x, min.x, Math.max(min.x, max.x)), p.y, THREE.MathUtils.clamp(p.z - ahead, min.y, Math.max(min.y, max.y)));
  }
  /** Holds a framing (indoors) or, with null, follows again. */
  frame(f: Framing | null): void { this.framing = f; }
  zoomBy(factor: number): void { this.distance = Math.min(C.maxDistance, Math.max(C.minDistance, this.distance * factor)); this.apply(); }
  setAspect(a: number): void { this.camera.aspect = a; this.camera.updateProjectionMatrix(); }
  attachGestures(el: HTMLElement): () => void {
    const onWheel = (e: WheelEvent) => { e.preventDefault(); this.zoomBy(Math.exp(e.deltaY * 0.0015)); };
    const pts = new Map<number, { x: number; y: number }>();
    let startDist = 0, startZoom = this.distance;
    const span = () => { const [a, b] = [...pts.values()]; return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0; };
    const down = (e: PointerEvent) => { pts.set(e.pointerId, { x: e.clientX, y: e.clientY }); if (pts.size === 2) { startDist = span(); startZoom = this.distance; } };
    const move = (e: PointerEvent) => {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 2 && startDist > 0) { this.distance = startZoom; this.zoomBy(startDist / Math.max(1, span())); }
    };
    const up = (e: PointerEvent) => { pts.delete(e.pointerId); startDist = 0; };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', down); el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
    return () => { el.removeEventListener('wheel', onWheel); el.removeEventListener('pointerdown', down); el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); };
  }
  private apply(): void {
    this.camera.position.copy(this.target).add(cameraOffset(C.pitchDeg, this.framing?.distance ?? this.distance));
    this.camera.lookAt(this.target);
    worldUniforms.uCamXZ.value.set(this.target.x, this.target.z);
  }
}
